import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import FlatsyButtonsField from '@flatsy/core/Form/ReactFormHooks/Field/Buttons'
import { TextError } from '@flatsy/core/Form/ReactFormHooks/Field/TextError'
import { Body1, Header1, LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import { FormValue } from '@flatsy/types/Applications'
import i18n from '@flatsy/utils/translations'
import { FormContext, ReturnContextProps } from 'src/containers/Qualification/Context'

import { useFormStyles } from '../../Style'
import { SharedProps } from '../../Type'
import useStyles from '../Style'
import { ProjectRentData } from './Type'
import { projectAdults, projectChildren, projectUnionType, projectWhen, projectWho } from './Utils'

const ProjectRent: React.FC<SharedProps> = ({ withOutApplication, renderMobileFormButton }) => {
  const formClasses = useFormStyles()
  const classes = useStyles()

  const schema = yup.object({
    availability_months: yup.string().required().typeError(TextError.empty),
    household_type: yup.string().required().typeError(TextError.empty),
    couple_union: yup
      .string()
      .nullable()
      .when('household_type', {
        is: (val: string) => [FormValue.who.COUPLE, FormValue.who.FAMILY].includes(val),
        then: yup.string().required().typeError(TextError.empty),
      }),
    household_adults: yup
      .string()
      .nullable()
      .when('household_type', {
        is: (val: string) => [FormValue.who.SHARED, FormValue.who.FAMILY].includes(val),
        then: yup.string().required().typeError(TextError.empty),
      }),
    household_children: yup
      .string()
      .nullable()
      .when('household_type', {
        is: (val: string) => [FormValue.who.FAMILY].includes(val),
        then: yup.string().required().typeError(TextError.empty),
      }),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitSuccessful, isValid, isDirty },
  } = useForm<ProjectRentData>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  const household_type = watch('household_type')

  const {
    wizardContext: { context },
    saveApplicationInRedux,
    saveApplicationAndContinue,
  } = useContext(FormContext) as ReturnContextProps
  const { application } = context

  useEffect(() => {
    if (application) {
      reset({
        availability_months: application.availability_months,
        household_type: application.household_type,
        couple_union: application.couple_union,
        household_adults: application.household_adults,
        household_children: application.household_children,
      })
    }
  }, [application])

  useEffect(() => {
    if (household_type) {
      setValue('household_children', undefined)
      setValue('household_adults', undefined)
    }
  }, [household_type])

  const onSubmit = async (values: ProjectRentData): Promise<undefined> => {
    const valuesFormated = { ...values }
    if (values.household_type === FormValue.who.COUPLE) {
      valuesFormated.household_adults = '2'
    }
    if (values.household_type === FormValue.who.SINGLE) {
      valuesFormated.household_adults = '1'
    }
    if (withOutApplication) {
      saveApplicationInRedux({
        ...valuesFormated,
      })
    } else {
      saveApplicationAndContinue({
        ...valuesFormated,
      })
    }
    return undefined
  }

  const isAlone = () => {
    if (
      household_type === FormValue.who.SINGLE ||
      household_type === FormValue.who.COUPLE ||
      household_type === '' ||
      !household_type
    ) {
      return true
    }

    return false
  }

  return (
    <div className={formClasses.container}>
      <div className={classes.intro}>
        <Header1 className={classes.introTitle} lowerCase align="center">
          {i18n.t('welcome')}
        </Header1>
        <Body1 align="center">
          {i18n.t('in_order_to_schedule_a_tour_can_you_tell_us_more_about_your_moving_project')}
        </Body1>
        <div className={classes.border} />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('when_do_you_wish_to_move_in')}
        </LabelSeparator>
        <Controller
          name="availability_months"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={projectWhen()}
            />
          )}
        />
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('who_are_you_moving_in_with')}
        </LabelSeparator>
        <Controller
          name="household_type"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={projectWho()}
            />
          )}
        />
        {[FormValue.who.COUPLE, FormValue.who.FAMILY].includes(household_type) && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {i18n.t('are_you_married_or_in_a_civil_union')}
            </LabelSeparator>
            <Controller
              name="couple_union"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={projectUnionType()}
                />
              )}
            />
          </>
        )}
        {!isAlone() && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {i18n.t('how_many_adults')}
            </LabelSeparator>
            <Controller
              name="household_adults"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={projectAdults()}
                />
              )}
            />
          </>
        )}
        {household_type === FormValue.who.FAMILY && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {i18n.t('and_how_many_children')}
            </LabelSeparator>
            <Controller
              name="household_children"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={projectChildren()}
                />
              )}
            />
          </>
        )}
        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isDirty, !isValid)
        )}
      </form>
    </div>
  )
}

export default ProjectRent
